import { z } from "zod";
import { entrySchema, LIMITS, MAX_TRACKS, trackSchema } from "./timeline";

/**
 * Les liens de partage filtres par pistes (#588). Le proprietaire cree un lien, choisit les pistes
 * que la personne verra, et envoie l'adresse `/v/<jeton>`. Le jeton (32 octets aleatoires, en
 * base64url) n'est rendu qu'une fois, a la creation ; la base ne garde que son SHA-256.
 *
 * Ce que le lien montre est calcule par le serveur (`visibility.ts`) : les points des pistes
 * choisies, jamais un point `private`, jamais une piste masquee. Lecture seule.
 */

/** Le plus de liens actifs (non revoques) qu'une timeline porte. */
export const MAX_SHARE_LINKS = 50;

/** 32 octets en base64url sans bourrage : 43 caracteres. Tout autre jeton est inconnu d'office. */
export const SHARE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

const trackIdsSchema = z
  .array(z.number().int().positive().max(2_147_483_647))
  .min(1, "Choisis au moins une piste.")
  .max(MAX_TRACKS, `${String(MAX_TRACKS)} pistes au plus.`);

const labelSchema = z
  .string()
  .trim()
  .min(1, "Dis pour qui est ce lien.")
  .max(LIMITS.shareLabel, `Libellé : ${String(LIMITS.shareLabel)} caractères au plus.`);

/** Cree un lien : pour qui (libelle prive, jamais montre a la personne) et au moins une piste. */
export const createShareLinkBodySchema = z.strictObject({
  label: labelSchema,
  trackIds: trackIdsSchema,
});
export type CreateShareLinkBody = z.infer<typeof createShareLinkBodySchema>;

/** Change les pistes d'un lien, ou son libelle : au moins un champ. Le jeton ne change pas. */
export const updateShareLinkBodySchema = z
  .strictObject({
    label: labelSchema.optional(),
    trackIds: trackIdsSchema.optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, "Rien à modifier.");
export type UpdateShareLinkBody = z.infer<typeof updateShareLinkBodySchema>;

/** Un lien vu par son proprietaire. Ni le jeton ni son hash n'y sont jamais. */
export const shareLinkSchema = z.object({
  id: z.number().int().positive(),
  label: z.string(),
  trackIds: z.array(z.number().int().positive()),
  createdAt: z.string(),
  revokedAt: z.string().nullable(),
  lastViewedAt: z.string().nullable(),
  viewCount: z.number().int().nonnegative(),
  /** Combien de points le lien montre en ce moment (calcule par le serveur ; 0 si revoque). */
  entryCount: z.number().int().nonnegative(),
});
export type ShareLink = z.infer<typeof shareLinkSchema>;

export const shareLinksSchema = z.object({ links: z.array(shareLinkSchema) });
export type ShareLinks = z.infer<typeof shareLinksSchema>;

/** La reponse a la creation : le lien, et son jeton, **rendu cette seule fois**. */
export const createdShareLinkSchema = z.object({
  link: shareLinkSchema,
  token: z.string().regex(SHARE_TOKEN_PATTERN),
});
export type CreatedShareLink = z.infer<typeof createdShareLinkSchema>;

/**
 * Ce que `GET /api/v/:token` rend : la frise en lecture seule, reduite aux pistes du lien. Pas de
 * role, pas de libelle, pas de proche, pas d'adresse : seulement les pistes choisies, leurs points
 * ouverts, le compteur « a dater » qui en est calcule et le nom du proprietaire (s'il en a un),
 * comme pour un proche `family`.
 */
export const sharedFriseSchema = z.object({
  ownerName: z.string().nullable(),
  tracks: z.array(trackSchema),
  entries: z.array(entrySchema),
  undatedCount: z.number().int().nonnegative(),
});
export type SharedFrise = z.infer<typeof sharedFriseSchema>;
